import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ChevronDown, ChevronRight, Circle, ClipboardList } from 'lucide-react';
import { moduleStripeClass, topicStripeClass } from './CurriculumCollapse';
import { moduleFlow } from '../../utils/moduleFlow';
import { courseTopicPath } from '../../utils/coursePaths';
import CourseScheduleInterviews from './CourseScheduleInterviews';

export interface CourseTocLab {
  id: number;
  name: string;
  slug: string;
  isCompleted?: boolean;
}

export interface CourseTocTopic {
  title: string;
  slug?: string | null;
  isCompleted?: boolean;
  displayOrder?: number;
  labs?: CourseTocLab[];
}

export interface CourseTocAssessment {
  id?: number;
  title: string;
  displayOrder?: number;
  passed?: boolean | null;
}

export interface CourseTocModule {
  id?: number;
  title: string;
  isCompleted?: boolean;
  topics: CourseTocTopic[];
  assessments?: CourseTocAssessment[];
}

interface CourseTocProps {
  courseSlug: string;
  modules: CourseTocModule[];
  currentTopicSlug?: string | null;
  currentModuleId?: number | null;
  currentAssessmentId?: number | null;
  interviewPackageCount?: number;
  courseId?: number;
  courseTitle?: string;
  /** When false, interview links are shown in the right aside instead. */
  showInterviewLinks?: boolean;
  onNavigate?: () => void;
}

const CourseToc: React.FC<CourseTocProps> = ({
  courseSlug,
  modules,
  currentTopicSlug,
  currentModuleId,
  currentAssessmentId,
  interviewPackageCount = 0,
  courseId,
  courseTitle,
  showInterviewLinks = true,
  onNavigate,
}) => {
  const currentModuleIndex = useMemo(
    () => modules.findIndex((module) =>
      (currentModuleId && module.id === currentModuleId)
      || module.topics.some((topic) => topic.slug === currentTopicSlug)
      || (module.assessments || []).some((assessment) => assessment.id === currentAssessmentId)),
    [modules, currentTopicSlug, currentModuleId, currentAssessmentId]
  );
  const [openIds, setOpenIds] = useState<number[]>(() => (currentModuleIndex >= 0 ? [currentModuleIndex] : [0]));

  useEffect(() => {
    if (currentModuleIndex < 0) return;
    setOpenIds([currentModuleIndex]);
  }, [currentModuleIndex]);

  const toggle = (index: number) => {
    setOpenIds((current) => current.length === 1 && current[0] === index ? [] : [index]);
  };

  return (
    <nav aria-label="Course contents" className="rounded-2xl border border-gray-200 bg-white p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Contents</p>
      {showInterviewLinks && interviewPackageCount > 0 && courseId && (
        <div className="mt-3">
          <CourseScheduleInterviews
            courseId={courseId}
            courseSlug={courseSlug}
            courseTitle={courseTitle || ''}
            interviewPackageCount={interviewPackageCount}
            compact
          />
        </div>
      )}
      <ul className="mt-3 space-y-2">
        {modules.map((module, index) => {
          const open = openIds.includes(index);
          const stripe = moduleStripeClass(index);
          const flow = moduleFlow(module.topics, module.assessments);
          return (
            <li key={`${module.title}-${index}`} className={stripe.box}>
              <button
                type="button"
                className={`w-full text-left px-3 py-2 flex items-start gap-2 ${stripe.hover}`}
                onClick={() => toggle(index)}
                aria-expanded={open}
              >
                {open ? <ChevronDown className={`h-4 w-4 mt-0.5 shrink-0 ${stripe.chevron}`} /> : <ChevronRight className={`h-4 w-4 mt-0.5 shrink-0 ${stripe.chevron}`} />}
                {module.isCompleted
                  ? <CheckCircle className="h-4 w-4 mt-0.5 text-success-500 shrink-0" />
                  : <Circle className={`h-4 w-4 mt-0.5 shrink-0 ${stripe.muted}`} />}
                <span className={`text-sm font-semibold ${stripe.title}`}>
                  {String(index + 1).padStart(2, '0')} {module.title}
                </span>
              </button>
              {open && (
                <ul className="px-2 pb-2 space-y-1">
                  {flow.map((item, fi) => {
                    const topicStripe = topicStripeClass(index, fi);
                    if (item.kind === 'assessment') {
                      const active = !!item.assessment.id && item.assessment.id === currentAssessmentId;
                      const body = (
                        <span className="flex items-start gap-2">
                          {item.assessment.passed
                            ? <CheckCircle className="h-4 w-4 mt-0.5 text-success-500 shrink-0" />
                            : <ClipboardList className={`h-4 w-4 mt-0.5 shrink-0 ${topicStripe.muted}`} />}
                          <span>Assessment: {item.assessment.title}</span>
                        </span>
                      );
                      return (
                        <li key={`assess-${item.assessment.id || fi}`}>
                          {item.assessment.id ? (
                            <Link
                              to={`/courses/${courseSlug}/assessments/${item.assessment.id}`}
                              onClick={onNavigate}
                              className={`block rounded-lg px-2 py-1.5 text-sm ${
                                active
                                  ? 'bg-white font-semibold shadow-sm text-gray-900'
                                  : `${topicStripe.title} ${topicStripe.hover}`
                              }`}
                            >
                              {body}
                            </Link>
                          ) : (
                            <span className={`block rounded-lg px-2 py-1.5 text-sm ${topicStripe.muted}`}>{body}</span>
                          )}
                        </li>
                      );
                    }

                    const topic = item.topic;
                    const active = !!topic.slug && topic.slug === currentTopicSlug
                      && (!currentModuleId || !module.id || module.id === currentModuleId);
                    const body = (
                      <span className="flex items-start gap-2">
                        {topic.isCompleted
                          ? <CheckCircle className="h-4 w-4 mt-0.5 text-success-500 shrink-0" />
                          : <Circle className={`h-4 w-4 mt-0.5 shrink-0 ${topicStripe.muted}`} />}
                        <span>{topic.title}</span>
                      </span>
                    );
                    return (
                      <li key={`${topic.slug || topic.title}-${fi}`}>
                        {topic.slug ? (
                          <Link
                            to={courseTopicPath(courseSlug, topic.slug, module.id)}
                            onClick={onNavigate}
                            className={`block rounded-lg px-2 py-1.5 text-sm ${
                              active
                                ? 'bg-white font-semibold shadow-sm text-gray-900'
                                : `${topicStripe.title} ${topicStripe.hover}`
                            }`}
                          >
                            {body}
                          </Link>
                        ) : (
                          <span className={`block rounded-lg px-2 py-1.5 text-sm ${topicStripe.muted}`}>{body}</span>
                        )}
                        {(topic.labs || []).length > 0 && (
                          <ul className="ml-6 mt-1 space-y-1">
                            {topic.labs!.map((lab) => (
                              <li key={lab.id}>
                                <Link
                                  to={lab.slug ? `/labs/${lab.slug}?course=${courseSlug}` : '/labs'}
                                  onClick={onNavigate}
                                  className="flex items-center gap-2 rounded-lg px-2 py-1 text-xs text-primary-800 hover:bg-white"
                                >
                                  {lab.isCompleted
                                    ? <CheckCircle className="h-3.5 w-3.5 text-success-500 shrink-0" />
                                    : <Circle className={`h-3.5 w-3.5 shrink-0 ${topicStripe.muted}`} />}
                                  <span>{lab.name}{lab.isCompleted ? ' · Completed' : ''}</span>
                                </Link>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default CourseToc;
